import React, { useCallback, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { motion } from 'framer-motion';
import { X } from 'lucide-react';

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: React.ReactNode;
  children?: React.ReactNode;
  footer?: React.ReactNode;
  size?: 'sm' | 'md' | 'lg';
  /** Element to focus when the modal opens (defaults to first focusable). */
  initialFocusRef?: React.RefObject<HTMLElement>;
  closeOnBackdrop?: boolean;
  hideClose?: boolean;
}

const SIZES: Record<NonNullable<ModalProps['size']>, string> = {
  sm: 'max-w-sm',
  md: 'max-w-lg',
  lg: 'max-w-2xl',
};

const FOCUSABLE =
  'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

let modalCounter = 0;

/**
 * Accessible dialog: role="dialog", aria-modal, labelled by its title,
 * focus trap, Esc to close, backdrop click to close, body-scroll lock, and
 * focus restoration on close. Rendered through a portal to <body>.
 */
const Modal: React.FC<ModalProps> = ({
  isOpen,
  onClose,
  title,
  children,
  footer,
  size = 'md',
  initialFocusRef,
  closeOnBackdrop = true,
  hideClose = false,
}) => {
  const dialogRef = useRef<HTMLDivElement>(null);
  const titleId = useRef(`modal-title-${(modalCounter += 1)}`).current;
  const previouslyFocused = useRef<HTMLElement | null>(null);

  const focusableEls = useCallback((): HTMLElement[] => {
    const root = dialogRef.current;
    if (!root) return [];
    return Array.from(root.querySelectorAll<HTMLElement>(FOCUSABLE)).filter(
      (el) => el.getAttribute('aria-hidden') !== 'true',
    );
  }, []);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onClose();
        return;
      }
      if (e.key !== 'Tab') return;
      const els = focusableEls();
      if (els.length === 0) {
        e.preventDefault();
        return;
      }
      const first = els[0];
      const last = els[els.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    },
    [onClose, focusableEls],
  );

  useEffect(() => {
    if (!isOpen) return undefined;
    previouslyFocused.current = document.activeElement as HTMLElement | null;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const t = setTimeout(() => {
      const target = initialFocusRef?.current || focusableEls()[0] || dialogRef.current;
      target?.focus();
    }, 0);

    return () => {
      clearTimeout(t);
      document.body.style.overflow = prevOverflow;
      previouslyFocused.current?.focus?.();
    };
  }, [isOpen, initialFocusRef, focusableEls]);

  if (!isOpen) return null;

  return createPortal(
    <div
      className="fixed inset-0 z-[90] flex items-center justify-center bg-black/50 p-4"
      onMouseDown={(e) => {
        if (closeOnBackdrop && e.target === e.currentTarget) onClose();
      }}
    >
      <motion.div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
        initial={{ opacity: 0, scale: 0.96, y: 8 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        onKeyDown={handleKeyDown}
        className={`w-full ${SIZES[size]} max-h-[85vh] overflow-y-auto rounded-2xl bg-white shadow-xl outline-none dark:bg-navy-800`}
      >
        <div className="flex items-start justify-between p-6 pb-4">
          <h2 id={titleId} className="pr-6 text-xl font-bold text-gray-900 dark:text-white">
            {title}
          </h2>
          {!hideClose && (
            <button
              type="button"
              onClick={onClose}
              aria-label="Close dialog"
              className="flex-shrink-0 text-gray-400 hover:text-gray-600 dark:hover:text-slate-200"
            >
              <X className="h-5 w-5" />
            </button>
          )}
        </div>
        {children && <div className="px-6 pb-4 text-sm text-gray-700 dark:text-slate-300">{children}</div>}
        {footer && (
          <div className="flex justify-end gap-3 border-t border-gray-100 px-6 py-4 dark:border-slate-800">
            {footer}
          </div>
        )}
      </motion.div>
    </div>,
    document.body,
  );
};

export default Modal;
